<template>
  <MModal
    @close="close"
    modal-class="md:max-w-xl"
    :can-be-dismissed="!IS_MOBILE_APP"
    mobile-fullscreen
  >
    <BackButton
      v-if="IS_MOBILE_APP"
      @back="close"
      :label="$t('currency_chart.description')"
      sticky
    />
    <div class="px-4 py-6 md:p-8 md:rounded-6">
      <div class="px-6">
        <p v-if="!IS_MOBILE_APP" class="text-16 lg:text-20">
          {{ $t('currency_chart.description') }}
        </p>
        <p class="mt-2 text-24 lg:text-30 font-bold mb-1">
          1 {{ currencyFromIso }} = {{ formatRate(midMarketRate) }}
          {{ currencyToIso }}
        </p>
        <ChartTrendMessage
          v-if="processedData && midMarketRate"
          :chart-data="processedData"
          :mid-market-rate="midMarketRate"
        />
        <CurrencyChart
          class="mt-6"
          :currency-from="currencyFromIso"
          :currency-to="currencyToIso"
          :mid-market-rate="midMarketRate"
          @chart-data="processChartData"
        />
        <button v-if="!IS_MOBILE_APP" class="mt-8" @click="getAlertClick">
          <svg role="presentation" focusable="false" tabindex="-1"  
            class="icon icon-md mr-2 text-gray-500"
            alt=""
            svg-inline
           xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 85 85"><path fill="currentColor" fill-rule="evenodd" d="M69.06 66.4H15.94a2.66 2.66 0 0 1 0-5.3h53.12a2.66 2.66 0 0 1 0 5.3ZM42.5 77.04a2.66 2.66 0 1 1 0-5.32 2.66 2.66 0 0 1 0 5.32Zm-21.25-37.2A21.27 21.27 0 0 1 42.5 18.6a21.27 21.27 0 0 1 21.25 21.25v15.94h-42.5V39.84Zm47.81 15.94V39.84a26.6 26.6 0 0 0-23.9-26.42v-8.1a2.66 2.66 0 1 0-5.32 0v8.1a26.6 26.6 0 0 0-23.9 26.42v15.94a7.98 7.98 0 0 0 0 15.94h19.08a7.8 7.8 0 0 0-.49 2.66 7.98 7.98 0 0 0 15.94 0c0-.94-.2-1.83-.5-2.66h19.1a7.98 7.98 0 0 0 0-15.94Z" clip-rule="evenodd"/></svg>
          <span class="text:16 lg:text-18 font-semibold underline">
            {{ $t('mmr_widget.track') }}
          </span>
        </button>
        <template v-else>
          <Transition
            leave-active-class="transition-opacity ease-out duration-200"
            enter-active-class="transition-opacity ease-in duration-300"
            leave-to-class="opacity-0"
            enter-from-class="opacity-0"
            mode="out-in"
          >
            <div
              v-if="!$pushNotificationsAllowed"
              class="flex justify-evenly bg-amber-50 p-4 mt-6 rounded-16"
            >
              <svg role="presentation" focusable="false" tabindex="-1"   class="icon icon-md mr-2" alt="" svg-inline  xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 85 85"><path fill="currentColor" fill-rule="evenodd" d="M69.06 66.4H15.94a2.66 2.66 0 0 1 0-5.3h53.12a2.66 2.66 0 0 1 0 5.3ZM42.5 77.04a2.66 2.66 0 1 1 0-5.32 2.66 2.66 0 0 1 0 5.32Zm-21.25-37.2A21.27 21.27 0 0 1 42.5 18.6a21.27 21.27 0 0 1 21.25 21.25v15.94h-42.5V39.84Zm47.81 15.94V39.84a26.6 26.6 0 0 0-23.9-26.42v-8.1a2.66 2.66 0 1 0-5.32 0v8.1a26.6 26.6 0 0 0-23.9 26.42v15.94a7.98 7.98 0 0 0 0 15.94h19.08a7.8 7.8 0 0 0-.49 2.66 7.98 7.98 0 0 0 15.94 0c0-.94-.2-1.83-.5-2.66h19.1a7.98 7.98 0 0 0 0-15.94Z" clip-rule="evenodd"/></svg>
              <div class="flex flex-col">
                <strong>{{ $t('mmr_widget.alert_prompt_title') }}</strong>
                <p>{{ $t('mmr_widget.alert_prompt_subtitle') }}</p>
                <button
                  class="btn btn-amber btn-sm mt-4 flex items-center justify-center"
                  @click="enableNotifications"
                >
                  <MSpinner
                    v-if="isLoading"
                    class="ml-2"
                    size="h-6 w-6"
                    stroke-width="3"
                    color="text-white"
                  />
                  <span v-else>{{ $t('mmr_widget.alert_prompt_cta') }}</span>
                </button>
              </div>
            </div>
            <div v-else class="flex bg-gray-100 p-4 mt-6 rounded-16">
              <div class="relative mr-3">
                <svg role="presentation" focusable="false" tabindex="-1"   class="icon icon-md" alt="" svg-inline  xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 85 85"><path fill="currentColor" fill-rule="evenodd" d="M69.06 66.4H15.94a2.66 2.66 0 0 1 0-5.3h53.12a2.66 2.66 0 0 1 0 5.3ZM42.5 77.04a2.66 2.66 0 1 1 0-5.32 2.66 2.66 0 0 1 0 5.32Zm-21.25-37.2A21.27 21.27 0 0 1 42.5 18.6a21.27 21.27 0 0 1 21.25 21.25v15.94h-42.5V39.84Zm47.81 15.94V39.84a26.6 26.6 0 0 0-23.9-26.42v-8.1a2.66 2.66 0 1 0-5.32 0v8.1a26.6 26.6 0 0 0-23.9 26.42v15.94a7.98 7.98 0 0 0 0 15.94h19.08a7.8 7.8 0 0 0-.49 2.66 7.98 7.98 0 0 0 15.94 0c0-.94-.2-1.83-.5-2.66h19.1a7.98 7.98 0 0 0 0-15.94Z" clip-rule="evenodd"/></svg>
                <svg role="presentation" focusable="false" tabindex="-1" class="absolute top-1 left-4 icon-xs rounded-full text-white bg-green-800"
                   
                  alt=""
                  svg-inline
                 xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 32 32">
<path fill="currentColor" fill-rule="evenodd" d="m11.33 24-5-6.67a1.66 1.66 0 1 1 2.67-2l3.77 5.03 10.3-11.79a1.67 1.67 0 1 1 2.52 2.2L13.92 24.1c-.7.8-1.95.75-2.59-.1" clip-rule="evenodd"/>
</svg>
              </div>
              <div class="flex flex-col">
                <div class="flex justify-between items-baseline">
                  <strong>{{ $t('mmr_widget.validation_title') }}</strong>
                  <NuxtLink to="/profile" class="text-14 font-semibold underline">{{
                    $t('mmr_widget.change')
                  }}</NuxtLink>
                </div>
                <p class="text-14 mt-2">{{ $t('mmr_widget.validation_description') }}</p>
              </div>
            </div>
          </Transition>
        </template>
      </div>
    </div>
  </MModal>
</template>

<script lang="ts" setup>
import { MidMarketModalOpen } from '@core/analytics/core-events/moneyTransfer'
import type { ChartData } from 'chart.js'
import { changedIn2months } from '@core/utils/chart-data'
import { formatRate } from '@core/utils/format'

const { $track, $pushNotificationsAllowed } = useNuxtApp()

const emit = defineEmits(['open-subscribe-modal', 'close'])

const IS_MOBILE_APP = __IS_MOBILE_APP__

defineProps<{
  midMarketRate: number
  currencyFromIso: string
  currencyToIso: string
}>()

const router = useRouter()
onMounted(async () => {
  await $track(MidMarketModalOpen())

  await router.push({ hash: '#midMarketRate' })
  const unregisterGuard = router.beforeEach((_to, _from, next) => {
    emit('close')
    unregisterGuard()
    next()
  })
})

const processedData = ref<Record<string, ChartData> | null>(null)

function getAlertClick() {
  close()
  emit('open-subscribe-modal')
}

function close() {
  router.go(-1)
}

function processChartData(chartData: Record<string, ChartData> | null) {
  if (chartData && changedIn2months(chartData)) {
    processedData.value = chartData
  }
}

const isLoading = ref(false)

function enableNotifications() {
  isLoading.value = true
  setTimeout(() => {
    isLoading.value = false
    $pushNotificationsAllowed.value = true
  }, 1000)
}
</script>
